import {
  HEART_REGENT_ROLES,
  applyHeartRegentCommand,
  createHeartRegentState,
  projectHeartRegentPublic,
  restoreHeartRegentState,
  serializeHeartRegentState,
  type HeartRegentAffiliation,
  type HeartRegentCommand,
  type HeartRegentManifest,
  type HeartRegentRole,
  type HeartRegentState,
  type HeartRegentVariant
} from "./heart-regent";

export const HEART_CENTRAL_RUNTIME_SCHEMA = "vaelor-heart-central-runtime/v2" as const;
export const HEART_CENTRAL_PUBLIC_SCHEMA = "vaelor-heart-central-public/v1" as const;
export const HEART_CENTRAL_PRIVATE_SCHEMA = "vaelor-heart-central-private/v1" as const;
export const HEART_CENTRAL_POLICY_ID = "r1-c70-i10-a6-b2-w120" as const;
export const HEART_CENTRAL_ROLES = HEART_REGENT_ROLES;
export type HeartCentralRole = HeartRegentRole;
export type HeartCentralPhase = "active" | "contained" | "repelled";
export type HeartCentralMovement = "manifestation" | "fracture" | "contention";

export const HEART_CENTRAL_POLICY = Object.freeze({
  policyId: HEART_CENTRAL_POLICY_ID,
  resistance: 1,
  coverageRequired: 0.7,
  individualCap: 1,
  activeAnchors: 6,
  toleratedBreaks: 2,
  contentionWindowSeconds: 120
} as const);

const HEART_CENTRAL_REGENT_VARIANT: HeartRegentVariant = Object.freeze({
  syntheticOnly: true,
  resistance: HEART_CENTRAL_POLICY.resistance,
  coverageRequired: HEART_CENTRAL_POLICY.coverageRequired,
  individualCap: HEART_CENTRAL_POLICY.individualCap,
  activeAnchors: HEART_CENTRAL_POLICY.activeAnchors,
  toleratedBreaks: HEART_CENTRAL_POLICY.toleratedBreaks,
  contentionWindowSeconds: HEART_CENTRAL_POLICY.contentionWindowSeconds
});

export interface HeartCentralGateEvidence {
  readonly frontId: string;
  readonly receiptId: string;
  readonly gateEpoch: number;
  readonly heldUntil: number;
}

export interface HeartCentralParticipant {
  readonly seat: number;
  readonly playerId: string;
  readonly lossAllocationKey: string;
  readonly survivorUnits: number;
  readonly affiliation: HeartRegentAffiliation;
  readonly roles: readonly HeartCentralRole[];
}

export interface HeartCentralManifest {
  readonly schema: "vaelor-heart-central-manifest/v2";
  readonly manifestId: string;
  readonly manifestDigest: string;
  readonly chronicleId: string;
  readonly convergenceId: string;
  readonly rulesVersion: string;
  readonly front6AssaultId: string;
  readonly front6ReportId: string;
  readonly policyId: typeof HEART_CENTRAL_POLICY_ID;
  readonly lockedAtMs: number;
  readonly gates: readonly HeartCentralGateEvidence[];
  readonly participants: readonly HeartCentralParticipant[];
}

interface HeartCentralCommandReceipt {
  readonly commandId: string;
  readonly fingerprint: string;
  readonly revision: number;
}

export interface HeartCentralPrivateReport {
  readonly schema: "vaelor-heart-central-private-report/v1";
  readonly reportId: string;
  readonly attempt: number;
  readonly outcome: "contained" | "repelled";
  readonly assignedRoles: readonly HeartCentralRole[];
  readonly completedRoles: readonly HeartCentralRole[];
  readonly settledAtMs: number;
}

export interface HeartCentralState {
  readonly schema: typeof HEART_CENTRAL_RUNTIME_SCHEMA;
  readonly manifest: HeartCentralManifest;
  readonly revision: number;
  readonly regentState: HeartRegentState;
  readonly commandReceipts: readonly HeartCentralCommandReceipt[];
  readonly reportsByPlayer: Readonly<Record<string, HeartCentralPrivateReport>>;
}

export interface HeartCentralPublicProjection {
  readonly schema: typeof HEART_CENTRAL_PUBLIC_SCHEMA;
  readonly phase: HeartCentralPhase;
  readonly movement: HeartCentralMovement;
  readonly attempt: number;
  readonly functions: readonly { readonly role: HeartCentralRole; readonly status: "pending" | "complete" }[];
  readonly anchors: { readonly activeCount: number; readonly requiredCount: 6; readonly toleratedBreaks: 2 };
  readonly contentionEndsAtMs: number | null;
  readonly verdict: "pending" | "contained" | "repelled";
  readonly nextAction: string;
}

export interface HeartCentralPrivateProjection {
  readonly schema: typeof HEART_CENTRAL_PRIVATE_SCHEMA;
  readonly phase: HeartCentralPhase;
  readonly movement: HeartCentralMovement;
  readonly attempt: number;
  readonly assignedRoles: readonly HeartCentralRole[];
  readonly completedRoles: readonly HeartCentralRole[];
  readonly availableRoles: readonly HeartCentralRole[];
  readonly canSettle: boolean;
  readonly canReopen: boolean;
  readonly report: HeartCentralPrivateReport | null;
  readonly nextAction: string;
}

export type HeartCentralCommand =
  | { readonly type: "contribute"; readonly commandId: string; readonly playerId: string; readonly role: HeartCentralRole; readonly nowMs: number }
  | { readonly type: "settle"; readonly commandId: string; readonly playerId: string; readonly nowMs: number };

export interface HeartCentralReduction {
  readonly state: HeartCentralState;
  readonly changed: boolean;
  readonly replayed: boolean;
}

const IDENTIFIER = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;
const MAX_COMMAND_RECEIPTS = 256;
const HEART_CENTRAL_AFFILIATIONS = Object.freeze([
  "coalition",
  "small-company",
  "independent",
  "relay"
] as const satisfies readonly HeartRegentAffiliation[]);

function fail(code: string): never { throw new Error(code); }
function isRecord(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}
function exactKeys(value: Record<string, unknown>, keys: readonly string[], code: string): void {
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  if (actual.length !== expected.length || actual.some((key, index) => key !== expected[index])) fail(code);
}
function identifier(value: unknown, code: string): string {
  if (typeof value !== "string" || !IDENTIFIER.test(value)) fail(code);
  return value;
}
function timestamp(value: unknown, code: string): number {
  if (!Number.isSafeInteger(value) || Number(value) < 0) fail(code);
  return Number(value);
}
function positiveInteger(value: unknown, code: string): number {
  if (!Number.isSafeInteger(value) || Number(value) < 1) fail(code);
  return Number(value);
}
function nonNegativeInteger(value: unknown, code: string): number {
  if (!Number.isSafeInteger(value) || Number(value) < 0) fail(code);
  return Number(value);
}
function role(value: unknown): HeartCentralRole {
  if (typeof value !== "string" || !(HEART_CENTRAL_ROLES as readonly string[]).includes(value)) fail("heart_central_role_invalid");
  return value as HeartCentralRole;
}
function affiliation(value: unknown): HeartRegentAffiliation {
  if (typeof value !== "string" || !(HEART_CENTRAL_AFFILIATIONS as readonly string[]).includes(value)) {
    fail("heart_central_participant_affiliation_invalid");
  }
  return value as HeartRegentAffiliation;
}
function stable(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value) ?? "null";
  if (Array.isArray(value)) return `[${value.map(stable).join(",")}]`;
  const source = value as Record<string, unknown>;
  return `{${Object.keys(source).sort().map((key) => `${JSON.stringify(key)}:${stable(source[key])}`).join(",")}}`;
}
function freezeDeep<T>(value: T): T {
  if (value !== null && typeof value === "object" && !Object.isFrozen(value)) {
    for (const child of Object.values(value as Record<string, unknown>)) freezeDeep(child);
    Object.freeze(value);
  }
  return value;
}

function normalizeGate(value: unknown): HeartCentralGateEvidence {
  if (!isRecord(value)) fail("heart_central_gate_invalid");
  exactKeys(value, ["frontId", "receiptId", "gateEpoch", "heldUntil"], "heart_central_gate_invalid");
  return freezeDeep({
    frontId: identifier(value.frontId, "heart_central_front_invalid"),
    receiptId: identifier(value.receiptId, "heart_central_gate_receipt_invalid"),
    gateEpoch: positiveInteger(value.gateEpoch, "heart_central_gate_epoch_invalid"),
    heldUntil: timestamp(value.heldUntil, "heart_central_gate_deadline_invalid")
  });
}

function normalizeParticipant(value: unknown): HeartCentralParticipant {
  if (!isRecord(value)) fail("heart_central_participant_invalid");
  exactKeys(value, ["seat", "playerId", "lossAllocationKey", "survivorUnits", "affiliation", "roles"], "heart_central_participant_invalid");
  if (!Array.isArray(value.roles)) fail("heart_central_participant_roles_invalid");
  const roles = value.roles.map(role);
  if (new Set(roles).size !== roles.length) fail("heart_central_participant_roles_invalid");
  return freezeDeep({
    seat: positiveInteger(value.seat, "heart_central_participant_seat_invalid"),
    playerId: identifier(value.playerId, "heart_central_player_invalid"),
    lossAllocationKey: identifier(value.lossAllocationKey, "heart_central_allocation_key_invalid"),
    survivorUnits: nonNegativeInteger(value.survivorUnits, "heart_central_surviving_force_invalid"),
    affiliation: affiliation(value.affiliation),
    roles
  });
}

export function normalizeHeartCentralManifest(value: unknown): HeartCentralManifest {
  if (!isRecord(value)) fail("heart_central_manifest_invalid");
  exactKeys(value, ["schema", "manifestId", "manifestDigest", "chronicleId", "convergenceId", "rulesVersion", "front6AssaultId", "front6ReportId", "policyId", "lockedAtMs", "gates", "participants"], "heart_central_manifest_invalid");
  if (value.schema !== "vaelor-heart-central-manifest/v2" || value.policyId !== HEART_CENTRAL_POLICY_ID) fail("heart_central_manifest_contract_invalid");
  if (!Array.isArray(value.gates) || value.gates.length !== 6 || !Array.isArray(value.participants) || value.participants.length < 2 || value.participants.length > 12) fail("heart_central_manifest_shape_invalid");
  const gates = value.gates.map(normalizeGate);
  const participants = value.participants.map(normalizeParticipant);
  if (new Set(gates.map((gate) => gate.frontId)).size !== 6 || new Set(gates.map((gate) => gate.receiptId)).size !== 6 ||
      new Set(participants.map((participant) => participant.playerId)).size !== participants.length ||
      new Set(participants.map((participant) => participant.lossAllocationKey)).size !== participants.length ||
      participants.some((participant, index) => participant.seat !== index + 1) || participants.every((participant) => participant.survivorUnits === 0)) {
    fail("heart_central_manifest_uniqueness_invalid");
  }
  const assignedRoles = participants.flatMap((participant) => participant.roles);
  if (assignedRoles.length !== HEART_CENTRAL_ROLES.length || HEART_CENTRAL_ROLES.some((currentRole) => assignedRoles.filter((candidate) => candidate === currentRole).length !== 1)) fail("heart_central_manifest_roles_invalid");
  const strongestForce = Math.max(...participants.map((participant) => participant.survivorUnits));
  const combatOwner = participants.find((participant) => participant.roles.includes("combattre"));
  if (!combatOwner || combatOwner.survivorUnits !== strongestForce || strongestForce <= 0) fail("heart_central_manifest_combat_capacity_invalid");
  return freezeDeep({
    schema: "vaelor-heart-central-manifest/v2" as const,
    manifestId: identifier(value.manifestId, "heart_central_manifest_id_invalid"),
    manifestDigest: identifier(value.manifestDigest, "heart_central_manifest_digest_invalid"),
    chronicleId: identifier(value.chronicleId, "heart_central_chronicle_invalid"),
    convergenceId: identifier(value.convergenceId, "heart_central_convergence_invalid"),
    rulesVersion: identifier(value.rulesVersion, "heart_central_rules_invalid"),
    front6AssaultId: identifier(value.front6AssaultId, "heart_central_assault_invalid"),
    front6ReportId: identifier(value.front6ReportId, "heart_central_report_invalid"),
    policyId: HEART_CENTRAL_POLICY_ID,
    lockedAtMs: timestamp(value.lockedAtMs, "heart_central_locked_at_invalid"),
    gates,
    participants
  });
}

function roleCapacity(manifest: HeartCentralManifest, participant: HeartCentralParticipant, currentRole: HeartCentralRole): number {
  if (!participant.roles.includes(currentRole)) fail("heart_central_role_not_assigned");
  return currentRole === "combattre" ? Math.max(1, Math.min(100, participant.survivorUnits)) : manifest.gates.length;
}
function regentParticipantId(participant: HeartCentralParticipant, currentRole: HeartCentralRole): string {
  return `central-seat-${participant.seat}-${currentRole}`;
}

export function heartCentralRegentManifest(manifestValue: unknown): HeartRegentManifest {
  const manifest = normalizeHeartCentralManifest(manifestValue);
  const participants = manifest.participants.flatMap((participant) => participant.roles.map((currentRole) => ({
    participantId: regentParticipantId(participant, currentRole),
    affiliation: participant.affiliation,
    role: currentRole,
    capacity: roleCapacity(manifest, participant, currentRole)
  })));
  const roleDemand = Object.fromEntries(HEART_CENTRAL_ROLES.map((currentRole) => {
    const participant = manifest.participants.find((candidate) => candidate.roles.includes(currentRole));
    if (!participant) fail("heart_central_manifest_roles_invalid");
    return [currentRole, roleCapacity(manifest, participant, currentRole)];
  })) as Record<HeartCentralRole, number>;
  return freezeDeep({
    synthetic: true,
    immutable: true,
    manifestId: manifest.manifestId,
    chronicleId: manifest.chronicleId,
    convergenceId: manifest.convergenceId,
    rulesVersion: manifest.rulesVersion,
    attempt: 1,
    lockedAtMs: manifest.lockedAtMs,
    participants,
    anchors: manifest.gates.map((gate) => gate.frontId),
    roleDemand
  });
}

function normalizeReport(value: unknown): HeartCentralPrivateReport {
  if (!isRecord(value)) fail("heart_central_report_invalid");
  exactKeys(value, ["schema", "reportId", "attempt", "outcome", "assignedRoles", "completedRoles", "settledAtMs"], "heart_central_report_invalid");
  if (value.schema !== "vaelor-heart-central-private-report/v1" || (value.outcome !== "contained" && value.outcome !== "repelled") || !Array.isArray(value.assignedRoles) || !Array.isArray(value.completedRoles)) fail("heart_central_report_invalid");
  const assignedRoles = value.assignedRoles.map(role);
  const completedRoles = value.completedRoles.map(role);
  if (new Set(assignedRoles).size !== assignedRoles.length || new Set(completedRoles).size !== completedRoles.length || completedRoles.some((candidate) => !assignedRoles.includes(candidate))) fail("heart_central_report_invalid");
  return freezeDeep({
    schema: "vaelor-heart-central-private-report/v1" as const,
    reportId: identifier(value.reportId, "heart_central_report_id_invalid"),
    attempt: positiveInteger(value.attempt, "heart_central_attempt_invalid"),
    outcome: value.outcome,
    assignedRoles,
    completedRoles,
    settledAtMs: timestamp(value.settledAtMs, "heart_central_settled_at_invalid")
  });
}

function normalizeReceipt(value: unknown): HeartCentralCommandReceipt {
  if (!isRecord(value)) fail("heart_central_command_receipt_invalid");
  exactKeys(value, ["commandId", "fingerprint", "revision"], "heart_central_command_receipt_invalid");
  return freezeDeep({
    commandId: identifier(value.commandId, "heart_central_command_id_invalid"),
    fingerprint: typeof value.fingerprint === "string" && value.fingerprint.length <= 512 ? value.fingerprint : fail("heart_central_command_fingerprint_invalid"),
    revision: positiveInteger(value.revision, "heart_central_command_revision_invalid")
  });
}

export function normalizeHeartCentralState(value: unknown): HeartCentralState {
  if (!isRecord(value)) fail("heart_central_state_invalid");
  exactKeys(value, ["schema", "manifest", "revision", "regentState", "commandReceipts", "reportsByPlayer"], "heart_central_state_invalid");
  if (value.schema !== HEART_CENTRAL_RUNTIME_SCHEMA || !Array.isArray(value.commandReceipts) || !isRecord(value.regentState) || !isRecord(value.reportsByPlayer)) fail("heart_central_state_invalid");
  const manifest = normalizeHeartCentralManifest(value.manifest);
  let regentState: HeartRegentState;
  try { regentState = restoreHeartRegentState(JSON.stringify(value.regentState)); }
  catch { fail("heart_central_regent_state_invalid"); }
  const expectedRegentManifest = heartCentralRegentManifest(manifest);
  if (regentState.manifest === null || stable(regentState.manifest) !== stable(expectedRegentManifest) || stable(regentState.variant) !== stable(HEART_CENTRAL_REGENT_VARIANT) || regentState.attempt !== 1 || regentState.phase === "awaiting_manifest") fail("heart_central_regent_state_invalid");
  const revision = positiveInteger(value.revision, "heart_central_revision_invalid");
  const commandReceipts = value.commandReceipts.map(normalizeReceipt);
  if (commandReceipts.length > MAX_COMMAND_RECEIPTS || commandReceipts.length !== revision || regentState.deliveries.length !== revision || new Set(commandReceipts.map((receipt) => receipt.commandId)).size !== commandReceipts.length || commandReceipts.some((receipt, index) => receipt.revision !== index + 1)) fail("heart_central_command_history_invalid");
  const reportsByPlayer = Object.fromEntries(Object.entries(value.reportsByPlayer).map(([playerId, reportValue]) => [identifier(playerId, "heart_central_report_player_invalid"), normalizeReport(reportValue)]));
  const terminal = regentState.phase === "contained" || regentState.phase === "repelled";
  if (Object.keys(reportsByPlayer).some((playerId) => !manifest.participants.some((participant) => participant.playerId === playerId)) || (!terminal && Object.keys(reportsByPlayer).length !== 0) || (terminal && Object.keys(reportsByPlayer).length !== manifest.participants.length)) fail("heart_central_reports_state_invalid");
  return freezeDeep({ schema: HEART_CENTRAL_RUNTIME_SCHEMA, manifest, revision, regentState, commandReceipts, reportsByPlayer });
}

function openFingerprint(playerId: string): string { return stable({ type: "open", playerId, attempt: 1 }); }
function regentEnvelope(state: HeartCentralState, revision: number, nowMs: number): Pick<HeartRegentCommand, "deliveryId" | "commandId" | "ordinal" | "atMs" | "chronicleId" | "manifestId" | "attempt"> {
  return {
    deliveryId: `central-delivery-${revision}`,
    commandId: `central-authority-command-${revision}`,
    ordinal: revision,
    atMs: nowMs,
    chronicleId: state.manifest.chronicleId,
    manifestId: state.manifest.manifestId,
    attempt: 1
  };
}

export function createHeartCentralState(manifestValue: unknown, commandIdValue: unknown, playerIdValue: unknown): HeartCentralState {
  const manifest = normalizeHeartCentralManifest(manifestValue);
  const commandId = identifier(commandIdValue, "heart_central_command_id_invalid");
  const playerId = identifier(playerIdValue, "heart_central_player_invalid");
  if (!manifest.participants.some((participant) => participant.playerId === playerId)) fail("heart_central_player_not_eligible");
  const regentManifest = heartCentralRegentManifest(manifest);
  const lockCommand: HeartRegentCommand = {
    deliveryId: "central-delivery-1", commandId: "central-authority-command-1", ordinal: 1,
    atMs: manifest.lockedAtMs, type: "lock_manifest", chronicleId: manifest.chronicleId,
    manifestId: manifest.manifestId, attempt: 1, manifest: regentManifest
  };
  const regentState = applyHeartRegentCommand(createHeartRegentState(HEART_CENTRAL_REGENT_VARIANT), lockCommand, manifest.lockedAtMs);
  if (regentState.phase !== "active" || regentState.manifest === null || regentState.rejections.length !== 0) fail("heart_central_regent_lock_failed");
  return normalizeHeartCentralState({
    schema: HEART_CENTRAL_RUNTIME_SCHEMA,
    manifest,
    revision: 1,
    regentState: JSON.parse(serializeHeartRegentState(regentState)),
    commandReceipts: [{ commandId, fingerprint: openFingerprint(playerId), revision: 1 }],
    reportsByPlayer: {}
  });
}

export function reopenHeartCentralState(stateValue: unknown, commandIdValue: unknown, playerIdValue: unknown): HeartCentralReduction {
  const state = normalizeHeartCentralState(stateValue);
  const commandId = identifier(commandIdValue, "heart_central_command_id_invalid");
  const playerId = identifier(playerIdValue, "heart_central_player_invalid");
  if (!state.manifest.participants.some((participant) => participant.playerId === playerId)) fail("heart_central_player_not_eligible");
  const previous = state.commandReceipts.find((receipt) => receipt.commandId === commandId);
  if (previous) {
    if (previous.fingerprint !== openFingerprint(playerId)) fail("heart_central_command_conflict");
    return { state, changed: false, replayed: true };
  }
  fail("heart_central_reopen_not_available");
}

function commandFingerprint(command: HeartCentralCommand): string {
  return command.type === "contribute"
    ? stable({ type: command.type, playerId: command.playerId, role: command.role, attempt: 1 })
    : stable({ type: command.type, playerId: command.playerId, attempt: 1 });
}
function roleComplete(state: HeartRegentState, currentRole: HeartCentralRole): boolean {
  const current = state.functions[currentRole];
  return current.thresholdMet && current.usefulContributionObserved;
}
function dependenciesMet(currentRole: HeartCentralRole, state: HeartRegentState): boolean {
  switch (currentRole) {
    case "observer": return true;
    case "purifier": return roleComplete(state, "observer");
    case "ravitailler": return roleComplete(state, "observer") && roleComplete(state, "purifier");
    case "tenir": return roleComplete(state, "observer") && roleComplete(state, "purifier") && roleComplete(state, "ravitailler");
    case "combattre": return roleComplete(state, "observer") && roleComplete(state, "purifier") && roleComplete(state, "ravitailler") && roleComplete(state, "tenir");
  }
}
function makeReports(state: HeartCentralState, nowMs: number): Record<string, HeartCentralPrivateReport> {
  const outcome = state.regentState.phase;
  if (outcome !== "contained" && outcome !== "repelled") fail("heart_central_report_outcome_invalid");
  return Object.fromEntries(state.manifest.participants.map((participant) => [participant.playerId, freezeDeep({
    schema: "vaelor-heart-central-private-report/v1" as const,
    reportId: `central-report-1-${participant.seat}`,
    attempt: 1,
    outcome,
    assignedRoles: participant.roles,
    completedRoles: participant.roles.filter((currentRole) => roleComplete(state.regentState, currentRole)),
    settledAtMs: nowMs
  })]));
}

export function reduceHeartCentral(stateValue: unknown, commandValue: HeartCentralCommand): HeartCentralReduction {
  const state = normalizeHeartCentralState(stateValue);
  if (!isRecord(commandValue)) fail("heart_central_command_invalid");
  const commandId = identifier(commandValue.commandId, "heart_central_command_id_invalid");
  const playerId = identifier(commandValue.playerId, "heart_central_player_invalid");
  const nowMs = timestamp(commandValue.nowMs, "heart_central_command_time_invalid");
  const participant = state.manifest.participants.find((candidate) => candidate.playerId === playerId);
  if (!participant) fail("heart_central_player_not_eligible");
  const fingerprint = commandFingerprint(commandValue);
  const previous = state.commandReceipts.find((receipt) => receipt.commandId === commandId);
  if (previous) {
    if (previous.fingerprint !== fingerprint) fail("heart_central_command_conflict");
    return { state, changed: false, replayed: true };
  }
  if (state.regentState.phase !== "active") fail("heart_central_attempt_terminal");
  if (state.commandReceipts.length >= MAX_COMMAND_RECEIPTS) fail("heart_central_command_history_full");
  const revision = state.revision + 1;
  let regentCommand: HeartRegentCommand;
  if (commandValue.type === "contribute") {
    const currentRole = role(commandValue.role);
    if (!participant.roles.includes(currentRole)) fail("heart_central_role_not_assigned");
    if (roleComplete(state.regentState, currentRole)) fail("heart_central_role_already_complete");
    if (!dependenciesMet(currentRole, state.regentState)) fail("heart_central_role_order_invalid");
    if (currentRole === "combattre" && state.regentState.windowOpenedAtMs !== null && nowMs > state.regentState.windowOpenedAtMs + HEART_CENTRAL_POLICY.contentionWindowSeconds * 1_000) fail("heart_central_contention_expired");
    const regentParticipant = state.regentState.manifest?.participants.find((candidate) => candidate.participantId === regentParticipantId(participant, currentRole));
    if (!regentParticipant || regentParticipant.role !== currentRole) fail("heart_central_regent_participant_invalid");
    regentCommand = {
      ...regentEnvelope(state, revision, nowMs),
      type: "contribute",
      participantId: regentParticipant.participantId,
      claimedRole: currentRole,
      units: regentParticipant.capacity,
      ...(currentRole === "tenir" ? { anchorIds: state.manifest.gates.map((gate) => gate.frontId) } : {})
    };
  } else {
    if (state.regentState.movement !== "contention") fail("heart_central_settlement_not_ready");
    regentCommand = { ...regentEnvelope(state, revision, nowMs), type: "settle" };
  }
  const previousRejections = state.regentState.rejections.length;
  const regentState = applyHeartRegentCommand(state.regentState, regentCommand, nowMs);
  if (regentState.deliveries.length !== state.regentState.deliveries.length + 1 || regentState.rejections.length !== previousRejections) fail("heart_central_regent_command_rejected");
  if (commandValue.type === "contribute" && !roleComplete(regentState, commandValue.role)) fail("heart_central_regent_contribution_incomplete");
  if (commandValue.type === "settle" && regentState.phase !== "contained" && regentState.phase !== "repelled") fail("heart_central_regent_settlement_incomplete");
  const nextBase = {
    schema: HEART_CENTRAL_RUNTIME_SCHEMA,
    manifest: state.manifest,
    revision,
    regentState,
    commandReceipts: [...state.commandReceipts, { commandId, fingerprint, revision }]
  };
  const next = normalizeHeartCentralState({
    ...nextBase,
    reportsByPlayer: commandValue.type === "settle" ? makeReports({ ...state, revision, regentState }, nowMs) : {}
  });
  return { state: next, changed: true, replayed: false };
}

function phaseOf(state: HeartCentralState): HeartCentralPhase {
  if (state.regentState.phase === "awaiting_manifest") fail("heart_central_regent_state_invalid");
  return state.regentState.phase;
}

export function projectHeartCentralPublic(stateValue: unknown): HeartCentralPublicProjection {
  const state = normalizeHeartCentralState(stateValue);
  const projection = projectHeartRegentPublic(state.regentState);
  const phase = phaseOf(state);
  if (!Number.isSafeInteger(projection.summary.anchors.activeCount) || projection.summary.anchors.activeCount < 0 ||
      projection.summary.anchors.activeCount > 6 || projection.summary.anchors.requiredCount !== 6 ||
      projection.summary.anchors.toleratedBreaks !== 2) fail("heart_central_anchor_projection_invalid");
  return freezeDeep({
    schema: HEART_CENTRAL_PUBLIC_SCHEMA,
    phase,
    movement: projection.summary.movement,
    attempt: projection.attempt ?? 1,
    functions: HEART_CENTRAL_ROLES.map((currentRole) => ({ role: currentRole, status: roleComplete(state.regentState, currentRole) ? "complete" as const : "pending" as const })),
    anchors: { activeCount: projection.summary.anchors.activeCount, requiredCount: 6 as const, toleratedBreaks: 2 as const },
    contentionEndsAtMs: state.regentState.contentionWindowEndsAtMs,
    verdict: projection.verdict,
    nextAction: phase === "contained"
      ? "Le Régisseur est contenu. La stabilisation du Trône reste fermée tant que son autorité n'est pas disponible."
      : phase === "repelled"
        ? "La tentative centrale est close. Attendre une nouvelle convergence serveur avant toute autre action."
        : "Accomplir les cinq fonctions complémentaires avant la fin de la Contention."
  });
}

export function projectHeartCentralPrivate(stateValue: unknown, playerIdValue: unknown, nowMsValue: unknown): HeartCentralPrivateProjection {
  const state = normalizeHeartCentralState(stateValue);
  const playerId = identifier(playerIdValue, "heart_central_player_invalid");
  timestamp(nowMsValue, "heart_central_projection_time_invalid");
  const participant = state.manifest.participants.find((candidate) => candidate.playerId === playerId);
  if (!participant) fail("heart_central_player_not_eligible");
  const phase = phaseOf(state);
  const assignedRoles = [...participant.roles];
  const completedRoles = assignedRoles.filter((currentRole) => roleComplete(state.regentState, currentRole));
  const availableRoles = phase === "active" ? assignedRoles.filter((currentRole) => !roleComplete(state.regentState, currentRole) && dependenciesMet(currentRole, state.regentState)) : [];
  const canSettle = phase === "active" && state.regentState.movement === "contention";
  const nextAction = phase === "contained"
    ? "Lire ton rapport. La prochaine étape sera de stabiliser le Trône, encore fermée dans cette version."
    : phase === "repelled"
      ? "Attendre une nouvelle convergence serveur; aucune réouverture locale n'est autorisée."
      : availableRoles.length > 0
        ? "Choisir l'une de tes fonctions disponibles."
        : canSettle
          ? "Demander le verdict autoritaire de la Contention."
          : "Attendre la fonction collective suivante, sans révéler le rôle d'un autre joueur.";
  return freezeDeep({
    schema: HEART_CENTRAL_PRIVATE_SCHEMA,
    phase,
    movement: state.regentState.movement,
    attempt: state.regentState.attempt ?? 1,
    assignedRoles,
    completedRoles,
    availableRoles,
    canSettle,
    canReopen: false,
    report: state.reportsByPlayer[playerId] ?? null,
    nextAction
  });
}

export function canonicalHeartCentralManifestInput(value: Omit<HeartCentralManifest, "manifestId" | "manifestDigest">): string { return stable(value); }
